import React, { useEffect, useRef } from 'react';
import Picture from './Picture';
import { localizedName, mediaUrl, type MediaLang } from './media';
import { useReducedMotion } from './useReducedMotion';

interface LoopVideoProps {
  /** Base name in public/media (`save-packing`, `earn-tablet`, …). */
  name: string;
  /** The file carries a `-de` / `-en` suffix that follows the page language. */
  localized?: boolean;
  /** A `-still-{960,1920}` image exists; it is the reduced-motion fallback. */
  hasStill?: boolean;
  /** Empty for decorative loops. */
  alt: string;
  lang: MediaLang;
  className?: string;
}

/**
 * A short muted loop (implementation brief §4). It loads nothing until it
 * scrolls into view (`preload="none"`, no `autoplay` attribute, which would
 * override preload), plays while visible and pauses when it leaves. Under
 * `prefers-reduced-motion` it renders the still or the poster instead.
 */
const LoopVideo: React.FC<LoopVideoProps> = ({ name, localized = false, hasStill = false, alt, lang, className }) => {
  const reduceMotion = useReducedMotion();
  const ref = useRef<HTMLVideoElement>(null);
  const base = localized ? localizedName(name, lang) : name;

  useEffect(() => {
    const video = ref.current;
    if (!video || typeof IntersectionObserver === 'undefined') return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          video.play().catch(() => {
            /* autoplay refused (data saver, low power): the poster stays */
          });
        } else {
          video.pause();
        }
      },
      { threshold: 0.25 }
    );
    observer.observe(video);
    return () => observer.disconnect();
  }, [base, reduceMotion]);

  if (reduceMotion) {
    if (hasStill) {
      return (
        <Picture
          className={className}
          name={`${base}-still`}
          widths={[960, 1920]}
          alt={alt}
          width={1920}
          height={1080}
          sizes="(max-width: 860px) 100vw, 50vw"
        />
      );
    }
    return (
      <picture className={className}>
        <source type="image/webp" srcSet={mediaUrl(`${base}-poster.webp`)} />
        <img src={mediaUrl(`${base}-poster.jpg`)} alt={alt} width={1280} height={720} decoding="async" />
      </picture>
    );
  }

  const label = alt ? { role: 'img', 'aria-label': alt } : { 'aria-hidden': true };
  return (
    <video
      key={base}
      ref={ref}
      className={className}
      muted
      playsInline
      loop
      preload="none"
      poster={mediaUrl(`${base}-poster.jpg`)}
      width={1280}
      height={720}
      {...label}
    >
      <source src={mediaUrl(`${base}.webm`)} type="video/webm" />
      <source src={mediaUrl(`${base}.mp4`)} type="video/mp4" />
    </video>
  );
};

export default LoopVideo;
